import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AMENITIES,
  COMMON_AMENITIES,
  DEFAULT_OFFER,
  RENT_CYCLES,
  SHORT_LET_PERIODS,
  UNIT_STATUSES,
  normaliseAmenities,
  normaliseCharges,
  normaliseOffers,
  pricingFor,
  type UnitCharge,
} from "@rentorng/core";
import { TermInput } from "@/components/term-input";
import { OfferPicker } from "@/components/offer-icons";
import { AmenityIcon } from "@/components/amenity-icons";
import { PendingUnitPhotos, UnitPhotos, uploadUnitPhotos } from "@/components/unit-photos";
import { UnitCharges } from "@/components/unit-charges";
import { IconCopy, IconExternal, IconEye, IconEyeOff, IconPlus, IconTrash } from "@/components/icons";
import { Refusal } from "@/components/padlock";
import { api } from "@/lib/api";
import { resolveParty, resolveProperty, searchParties, searchProperties } from "@/lib/pickers";
import { SearchSelect } from "@/components/search-select";
import { formValues, nullIfBlank, useApiMutation } from "@/lib/mutations";
import { major, minor, titleCase } from "@/lib/format";
import { useSession } from "@/lib/session";
import {
  Alert,
  Button,
  Card,
  CardTitle,
  Field,
  FormSection,
  Input,
  PageHeader,
  Modal,
  Select,
  Spinner,
  Switch,
  Textarea,
} from "@/components/ui";
import { MoneyInput } from "@/components/money-input";

/**
 * One screen, two ways in, reached from two places.
 *
 * The single form is for a one-off. The bulk form exists because a block arrives as "Flat 1 to
 * Flat 24 at the same rent", and making someone fill in a form 24 times is how a migration
 * stalls on its first afternoon.
 *
 * It is reached either from a property — which fixes the property and hides the question — or
 * from the unit list, where the property has to be asked for. The second route exists because
 * "add a unit" is a thought people have while looking at their units, not while looking at a
 * building, and making them navigate to the building first is a detour with no purpose.
 */
export function UnitFormPage() {
  const { id, propertyId } = useParams();
  const navigate = useNavigate();
  const { vocab, org, listingsUrl, can } = useSession();
  const editing = Boolean(id);
  /** Only used on the property-less route — from a property, the answer is already known. */
  const [chosenProperty, setChosenProperty] = useState("");
  /** Blank means the building's landlord owns it, which is the ordinary case. ADR-0024. */
  const [ownerId, setOwnerId] = useState("");
  /**
   * The bulk run keeps its own.
   *
   * Both forms are on screen together, and they shared this: choosing an owner for a run of flats
   * changed the owner on the single form beside it, with nothing to say so.
   */
  const [bulkOwnerId, setBulkOwnerId] = useState("");
  /** The run's own, for the same reason the owner is: two forms, two answers. */
  const [bulkListed, setBulkListed] = useState(true);
  /**
   * What it is on the market as.
   *
   * Never emptied by the last click: a unit with no offer disappears from every filter, which
   * reads as deleted rather than as nobody having chosen.
   */
  const [offers, setOffers] = useState<string[]>([DEFAULT_OFFER]);
  /** Amenities are a set, in the published order, and an empty one is perfectly ordinary. */
  const [amenities, setAmenities] = useState<string[]>([]);
  /**
   * Whether the world gets to see it once it is empty.
   *
   * On for anything new, because an agency adding a vacant unit almost always wants it found and
   * a listing site nobody's property reaches is not a listing site. Held in state rather than read
   * off the form at submit, because the panel it lives in changes with it.
   */
  const [listed, setListed] = useState(true);
  /**
   * Photographs chosen before there is anything to attach them to.
   *
   * Held here and posted the moment the unit has an id. Nobody should have to save a form, find
   * their way back to it and start again with the file picker — that is the server's constraint
   * being handed to the person filling the form in.
   */
  const [pendingPhotos, setPendingPhotos] = useState<File[]>([]);

  /**
   * What is payable besides the price, per offer.
   *
   * Held in state rather than read off the form on submit, because a percentage has to be shown as
   * the money it comes to while it is being typed — "10%" is not a figure anybody can check, and
   * "= ₦18,000,000" is.
   */
  const [charges, setCharges] = useState<UnitCharge[]>([]);
  const [bulkCharges, setBulkCharges] = useState<UnitCharge[]>([]);

  /**
   * The prices, held rather than read off the form on submit.
   *
   * Everything else on this form can be read at the end, because nothing depends on it until then.
   * The prices are different: a charge quoted at ten per cent has to say what ten per cent comes
   * to *while the price is being typed*, or the figure it resolves to is a surprise on the listing.
   *
   * Kept as the major-unit strings `MoneyInput` speaks, converted once at the edges.
   */
  const [rentInput, setRentInput] = useState("");
  const [leaseInput, setLeaseInput] = useState("");
  const [saleInput, setSaleInput] = useState("");
  const [shortLetInput, setShortLetInput] = useState("");
  /**
   * The run of units, behind a button.
   *
   * Two forms side by side is how somebody fills in one and submits the other, and it cost the
   * single form — the one nearly everybody wants — half the screen. A run of twenty flats is the
   * rarer job, so it waits behind its own button and opens over the page.
   */
  const [bulkOpen, setBulkOpen] = useState(false);
  const [photoProblem, setPhotoProblem] = useState<string | null>(null);
  /** Shown after a save that stayed on the page, and cleared a few seconds later. */
  const [saved, setSaved] = useState(false);
  const [bulkAmenities, setBulkAmenities] = useState<string[]>([]);

  const [leaseCycle, setLeaseCycle] = useState<string>("annual");
  const [leaseEvery, setLeaseEvery] = useState(1);
  const [cycle, setCycle] = useState("annual");
  const [every, setEvery] = useState(1);
  const [bulkCycle, setBulkCycle] = useState("annual");
  /** A run of flats created together is nearly always on the market as the same thing. */
  const [bulkOffers, setBulkOffers] = useState<string[]>([DEFAULT_OFFER]);
  const [bulkEvery, setBulkEvery] = useState(1);
  const [ownerLoaded, setOwnerLoaded] = useState(false);

  const { data: existing, isLoading } = useQuery({
    queryKey: ["unit", id],
    queryFn: () =>
      api.get<{
        unit: Record<string, string | number | boolean | null>;
        propertyName: string;
        propertyId: string;
        propertyLandlordPartyId: string | null;
        /** The address it has on the public site, or null when it is not on the market. */
        publicSlug: string | null;
        /** Present only while a listing is held for review or has been refused. ADR-0078. */
        review: { headline: string; detail: string } | null;
      }>(`/units/${id}`),
    enabled: editing,
  });

  const targetProperty = propertyId ?? (existing?.propertyId as string | undefined) ?? (chosenProperty || undefined);

  // Seed the owner picker once, when editing. Guarded because the user may deliberately clear it
  // back to "same as the building", and a second seed would silently undo that.
  if (existing && !ownerLoaded) {
    setOwnerLoaded(true);
    // Pre-selected with the owner this unit actually has — its own if it names one, otherwise
    // the building's. Saving then writes that choice down rather than leaving a blank that means
    // "whoever owns the building today", which is not what somebody who opened the form and saw a
    // name expects to have saved.
    setOwnerId(
      String(
        (existing.unit as Record<string, unknown>).landlordPartyId ??
          (existing as Record<string, unknown>).propertyLandlordPartyId ??
          "",
      ),
    );
    setCycle(String((existing.unit as Record<string, unknown>).rentCycle ?? "annual"));
    setEvery(Number((existing.unit as Record<string, unknown>).rentCycleEvery ?? 1) || 1);
    // Opens on what the unit already is, so editing does not silently reset it to rent.
    setOffers(normaliseOffers((existing.unit as Record<string, unknown>).offers as string[] | null));
    setAmenities(normaliseAmenities((existing.unit as Record<string, unknown>).amenities as string[] | null));
    // Editing shows what the unit is actually set to; only a new one starts switched on.
    setListed(Boolean((existing.unit as Record<string, unknown>).listed));
    setLeaseCycle(String((existing.unit as Record<string, unknown>).leaseCycle ?? "annual"));
    setLeaseEvery(Number((existing.unit as Record<string, unknown>).leaseCycleEvery ?? 1) || 1);
    // The prices and the charges are held rather than left to `defaultValue`, because a percentage
    // has to show what it comes to against a price that is still being typed.
    const saved = existing.unit as Record<string, unknown>;
    setRentInput(major(saved.rentAmount));
    setLeaseInput(major(saved.leaseAmount));
    setSaleInput(major(saved.salePrice));
    setShortLetInput(major(saved.shortLetRate));
    setCharges(normaliseCharges(saved.charges as UnitCharge[]));
  }

  /**
   * Another one like this one.
   *
   * A block is twenty flats that differ in a door number, and the copy arrives with the listing
   * switch off — a duplicate is a draft until somebody has looked at it. Lands on the copy's own
   * form, because the first thing anybody does next is change the label.
   */
  const duplicate = useApiMutation({
    mutationFn: () => api.post<{ id: string; label: string }>(`/units/${id}/duplicate`, {}),
    invalidates: [["units"], ["property", targetProperty ?? ""], ["dashboard"]],
    onSuccess: (made: { id?: string } | undefined) => {
      if (made?.id) navigate(`/app/units/${made.id}/edit?copied=1`);
    },
  });

  const save = useApiMutation({
    mutationFn: (input: Record<string, unknown>) =>
      editing ? api.patch<{ id: string }>(`/units/${id}`, input) : api.post<{ id: string }>("/units", input),
    invalidates: [["units"], ["property", targetProperty ?? ""], ["dashboard"], ["unit", id ?? ""]],
    /**
     * Saving keeps you on the page, and says it worked.
     *
     * Editing a unit is rarely one change. Somebody fixes the rent, saves, and then wants to fix
     * the description — and being thrown back to the property list after each one made a five-field
     * edit into five journeys. It stays put and puts a line at the top instead.
     *
     * A **new** unit still lands on its own edit page, for a different reason: photographs attach
     * to an id and there is none until it is saved, so leaving before that means the listing goes
     * out with no picture on it.
     */
    onSuccess: async (created: { id?: string } | undefined) => {
      // The photographs were chosen before the unit existed; now it does, so they go up.
      if (!editing && created?.id && pendingPhotos.length) {
        const failed = await uploadUnitPhotos(created.id, pendingPhotos);
        if (failed) {
          // The unit is saved either way — say so, and stay put rather than losing the message.
          setPhotoProblem(`${failed} The ${vocab.unit} itself was saved.`);
          return;
        }
      }

      if (editing) {
        // Cleared on the next edit, so it is a confirmation of what just happened rather than a
        // banner that sits there for the rest of the session claiming everything is fine.
        setSaved(true);
        window.setTimeout(() => setSaved(false), 4000);
        return;
      }

      navigate(created?.id ? `/app/units/${created.id}/edit?added=1` : targetProperty ? `/app/properties/${targetProperty}` : "/app/units");
    },
  });

  /**
   * Removing this unit, from the page it is edited on.
   *
   * `property.delete` and nothing less, and the API checks the same thing again — the button only
   * decides whether to offer it. Ownership is the API's business: `owned()` refuses an id from
   * another agency whatever this screen believes.
   *
   * It leaves for the property it belonged to, because the page it was on no longer describes
   * anything.
   */
  const remove = useApiMutation({
    mutationFn: () => api.delete(`/units/${id}`),
    invalidates: [["units"], ["property", targetProperty ?? ""], ["dashboard"]],
    onSuccess: () => navigate(targetProperty ? `/app/properties/${targetProperty}` : "/app/units"),
  });

  const bulk = useApiMutation({
    mutationFn: (input: Record<string, unknown>) => api.post<{ created: number; skipped: number }>("/units/bulk", input),
    invalidates: [["units"], ["property", targetProperty ?? ""], ["dashboard"]],
    onSuccess: () => navigate(targetProperty ? `/app/properties/${targetProperty}` : "/app/units"),
  });

  if (editing && isLoading) return <Spinner />;
  const unit = (existing?.unit ?? {}) as Record<string, string | number | boolean | null>;
  const publicSlug = existing?.publicSlug ?? null;
  const review = existing?.review ?? null;

  const submitSingle = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    save.mutate({
      propertyId: targetProperty,
      landlordPartyId: ownerId || null,
      label: values.label,
      status: values.status,
      floor: nullIfBlank(values.floor),
      bedrooms: nullIfBlank(values.bedrooms),
      bathrooms: nullIfBlank(values.bathrooms),
      sizeSqm: nullIfBlank(values.sizeSqm),
      toilets: nullIfBlank(values.toilets),
      amenities,
      offers,
      leaseAmount: minor(leaseInput),
      leaseCycle,
      leaseCycleEvery: leaseEvery,
      salePrice: minor(saleInput),
      shortLetRate: minor(shortLetInput),
      shortLetPer: values.shortLetPer,
      rentAmount: minor(rentInput),
      rentCycle: cycle,
      rentCycleEvery: every,
      charges: normaliseCharges(charges, offers),
      description: nullIfBlank(values.description),
      listed,
    });
  };

  const submitBulk = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const values = formValues(event.currentTarget);
    bulk.mutate({
      propertyId: targetProperty,
      landlordPartyId: bulkOwnerId || null,
      prefix: values.prefix,
      from: values.from,
      to: values.to,
      offers: bulkOffers,
      rentAmount: nullIfBlank(values.bulkRent),
      rentCycle: bulkCycle,
      rentCycleEvery: bulkEvery,
      bedrooms: nullIfBlank(values.bulkBedrooms),
      bathrooms: nullIfBlank(values.bulkBathrooms),
      toilets: nullIfBlank(values.bulkToilets),
      amenities: bulkAmenities,
      charges: normaliseCharges(bulkCharges, bulkOffers),
      listed: bulkListed,
    });
  };

  return (
    /*
      The full width of the content area, not a measure of its own.

      A form is usually kept narrow because a long line of prose is hard to read back, but almost
      nothing here is prose: it is labelled boxes, tiles and figures, laid out two and four across.
      Held at `68rem` on a wide screen the page was a column of form with an empty third of the
      window beside it, and the tiles wrapped sooner than they needed to.

      `pb-24` because the buttons at the foot are pinned and take no space in the flow.
    */
    <div className="pb-24">
      <PageHeader
        title={editing ? `Edit ${unit.label ?? vocab.unit}` : `Add ${vocab.units}`}
        back={
          targetProperty
            ? { to: `/app/properties/${targetProperty}`, label: existing?.propertyName ?? titleCase(vocab.property) }
            : { to: "/app/units", label: titleCase(vocab.units) }
        }
        /*
          What a stranger sees, one press away.

          The form is where the advert is written, so it is where somebody wants to check how it
          reads — and the card in the list already offers this, which made its absence here the
          odd thing. It is a link out rather than a form action, so it sits in the header with the
          other navigation and not among Save and Cancel.

          Drawn only when there is a page to open: the server says whether this unit is on the
          market at all, by handing over a slug or a null.
        */
        actions={
          editing && listingsUrl ? (
            /*
              Live, or how it would read if it were.

              `publicSlug` is the server's answer to "does this have a public page" — it is null for
              anything not listed and vacant. So the same corner offers whichever of the two
              questions can actually be asked: **View** when a stranger can already see it, and
              **Preview** when they cannot. The preview opens the same page from the same shaping,
              on an address only this agency can load.
            */
            <a
              className="btn btn-secondary btn-sm"
              href={publicSlug ? `${listingsUrl}/listing/${publicSlug}` : `${listingsUrl}/preview/${id}`}
              target="_blank"
              rel="noreferrer"
              title={
                publicSlug
                  ? "Open the live listing"
                  : "See how this will read once it is published — nobody else can open it"
              }
            >
              <IconExternal size={15} />
              {publicSlug ? "View on site" : "Preview on site"}
            </a>
          ) : null
        }
      />

      {/*
        A listing that is switched on and still not public.

        Above the form and not beside the listing switch: the switch says what the agency asked
        for, and this says what is actually true — which is a different sentence and the one they
        will otherwise ring up about. Its words come from the server, so this, the card and the
        console cannot drift apart.
      */}
      {review ? (
        <div className="mb-5">
          <Alert tone="warn" title={review.headline}>
            {review.detail}
          </Alert>
        </div>
      ) : null}

      {!editing && !propertyId ? (
        <Card className="mb-5">
          <Field
            label={titleCase(vocab.property)}
            htmlFor="propertyId"
            required
            hint={`Which ${vocab.property} do these ${vocab.units} belong to?`}
          >
            <SearchSelect
              id="propertyId"
              value={chosenProperty}
              onChange={(next) => setChosenProperty(next)}
              search={searchProperties}
              resolve={resolveProperty}
              placeholder={`Search for a ${vocab.property}…`}
              emptyMessage={`No ${vocab.property} matched.`}
            />
          </Field>
        </Card>
      ) : null}

      {/* `items-start` so the shorter form keeps its own height instead of being stretched to
          match the taller one and ending in a field of white. */}
      <div>
        <Card>
          {editing ? null : (
            <div className="-mx-5 mb-5 border-b border-ink-200 px-5 sm:-mx-6 sm:px-6">
              <CardTitle
                hint="For a single flat, shop or house."
                action={
                  <Button variant="secondary" size="sm" onClick={() => setBulkOpen(true)}>
                    <IconPlus size={15} />
                    Add many at once
                  </Button>
                }
              >
                Add one
              </CardTitle>
            </div>
          )}
          <form onSubmit={submitSingle} className="space-y-6">
            {saved ? <Alert tone="success">Saved.</Alert> : null}
            {/*
              Four of the writes on this form can be refused by the plan rather than by anything
              wrong with what was typed: editing a padlocked unit, deleting one, putting one on the
              market, and adding a unit to a padlocked building. Each of those comes back carrying
              the server's own gate, and `Refusal` draws that as the padlock and the way out of it —
              a red box saying no to somebody who could simply pay is the one refusal in the product
              that is worth money to get right.
            */}
            <Refusal error={remove.error} message={remove.errorMessage} />
            <Refusal error={save.error} message={save.errorMessage} />
            <Refusal error={duplicate.error} message={duplicate.errorMessage} />
            {photoProblem ? <Alert tone="warn">{photoProblem}</Alert> : null}

            {/*
              The one decision on this form that changes who can see the unit at all, so it is at
              the top rather than the bottom, and it is a switch rather than a tick box — a tick
              box is part of filling a form in, a switch is a thing being turned on. The panel
              answers back: it says what is true now, not what flipping it would do.
            */}
            <div
              className={`flex items-start gap-4 rounded-[var(--radius-surface)] border p-4 transition-colors ${
                listed ? "border-brand-200 bg-brand-50" : "border-ink-200 bg-ink-50"
              }`}
            >
              <span
                className={`flex size-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                  listed ? "bg-brand-100 text-brand-700" : "bg-ink-200 text-ink-500"
                }`}
              >
                {listed ? <IconEye size={18} /> : <IconEyeOff size={18} />}
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-900">List publicly while vacant</p>
                <p className="mt-0.5 text-xs leading-relaxed text-ink-600">
                  {listed
                    ? "Shown on your public vacancy page, with your name and number on it, for as long as it is empty."
                    : "Kept off the public page. Only your team will see it."}
                </p>
              </div>

              <Switch checked={listed} onChange={setListed} label="List publicly while vacant" />
            </div>

            {/*
              Two columns: the record on the left, the advert on the right.

              Everything on the left is a fact with one right answer and a figure attached — what
              it is called, how many bedrooms, what it costs. The right is what a stranger sees:
              the photographs, the words, what the place has, and who owns it. Stacked, the writing
              sat below a screen and a half of number fields with half the page empty beside it.

              The amenities are on the right rather than the left for the same reason. They read as
              a specification, but nobody fills them in to keep a record — they are ticked so the
              advert says "borehole, gated estate", which is what somebody decides on before they
              read a word of the description. Putting them there also leaves the two columns
              roughly the same length, so neither ends in a field of white.

              One column on anything narrower than a laptop, in the reading order above.
            */}
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] xl:gap-10 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
              <div className="space-y-6">
                <FormSection title="The basics" hint="What it is called, and where in the building it is.">
                  <Field label="Label" htmlFor="label" required hint="Whatever you call it on the keys — “Flat 3B”, “Shop 12”.">
                    <Input id="label" name="label" required defaultValue={String(unit.label ?? "")} placeholder="Flat 3B" />
                  </Field>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Status" htmlFor="status">
                      <Select id="status" name="status" defaultValue={String(unit.status ?? "vacant")}>
                        {UNIT_STATUSES.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field label="Floor" htmlFor="floor">
                      <Input id="floor" name="floor" defaultValue={String(unit.floor ?? "")} placeholder="Ground" />
                    </Field>
                  </div>
                </FormSection>

                {/* Four counts, not three: a guest toilet is counted separately on every sign in
                    this market, and leaving it out gets an agency a phone call rather than a viewing. */}
                <FormSection title="The space" hint="What a viewer filters on. Anything not settled yet can be left empty.">
                  <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                    <Field label="Bedrooms" htmlFor="bedrooms">
                      <Input id="bedrooms" name="bedrooms" inputMode="numeric" placeholder="3" defaultValue={unit.bedrooms === null || unit.bedrooms === undefined ? "" : String(unit.bedrooms)} />
                    </Field>
                    <Field label="Bathrooms" htmlFor="bathrooms">
                      <Input id="bathrooms" name="bathrooms" inputMode="numeric" placeholder="2" defaultValue={unit.bathrooms === null || unit.bathrooms === undefined ? "" : String(unit.bathrooms)} />
                    </Field>
                    <Field label="Toilets" htmlFor="toilets">
                      <Input
                        id="toilets"
                        name="toilets"
                        inputMode="numeric"
                        placeholder="3"
                        defaultValue={unit.toilets === null || unit.toilets === undefined ? "" : String(unit.toilets)}
                      />
                    </Field>
                    <Field label="Size (sqm)" htmlFor="sizeSqm">
                      <Input id="sizeSqm" name="sizeSqm" inputMode="numeric" placeholder="85" defaultValue={unit.sizeSqm === null || unit.sizeSqm === undefined ? "" : String(unit.sizeSqm)} />
                    </Field>
                  </div>
                </FormSection>

                {/*
                  What the unit is on the market as, and then only the prices that apply.
                  A sale has no cycle and a joint venture has no price; showing every field at once
                  asks for figures that would mean nothing on the listing.

                  The heading is the group's own label — `group` puts the role on the section, so the
                  tiles are announced under the words above them rather than under a second copy.
                */}
                <FormSection
                  title="On the market as"
                  hint="Pick every one that applies — a unit can be to let and for sale at once. The prices follow."
                  group
                >
                  <OfferPicker value={offers} onChange={setOffers} />

                  {/* One price block per offer that carries a price, in a stable order. */}
                  {pricingFor(offers).map((entry) => (
                    <div key={entry.value} className="grid gap-4 sm:grid-cols-2">
                      {entry.value === "rent" ? (
                        <>
                          <Field label={entry.priceLabel} htmlFor="rentAmount">
                            <MoneyInput
                              symbol
                              id="rentAmount"
                              value={rentInput}
                              onChange={setRentInput}
                              placeholder="3,500,000"
                            />
                          </Field>
                          <Field label="Paid" htmlFor="rentCycle">
                            <TermInput cycle={cycle} every={every} onCycle={setCycle} onEvery={setEvery} />
                          </Field>
                        </>
                      ) : null}

                      {entry.value === "lease" ? (
                        <>
                          <Field label={entry.priceLabel} htmlFor="leaseAmount">
                            <MoneyInput
                              symbol
                              id="leaseAmount"
                              value={leaseInput}
                              onChange={setLeaseInput}
                              placeholder="30,000,000"
                            />
                          </Field>
                          <Field label="Over" htmlFor="leaseCycle">
                            <TermInput
                              idPrefix="leaseCycle"
                              cycle={leaseCycle}
                              every={leaseEvery}
                              onCycle={setLeaseCycle}
                              onEvery={setLeaseEvery}
                            />
                          </Field>
                        </>
                      ) : null}

                      {entry.value === "sale" ? (
                        <Field label={entry.priceLabel} htmlFor="salePrice">
                          <MoneyInput
                            symbol
                            id="salePrice"
                            value={saleInput}
                            onChange={setSaleInput}
                            placeholder="250,000,000"
                          />
                        </Field>
                      ) : null}

                      {entry.value === "short_let" ? (
                        <>
                          <Field label={entry.priceLabel} htmlFor="shortLetRate">
                            <MoneyInput
                              symbol
                              id="shortLetRate"
                              value={shortLetInput}
                              onChange={setShortLetInput}
                              placeholder="85,000"
                            />
                          </Field>
                          <Field label="Charged" htmlFor="shortLetPer">
                            <Select id="shortLetPer" name="shortLetPer" defaultValue={String(unit.shortLetPer ?? "night")}>
                              {SHORT_LET_PERIODS.map((period) => (
                                <option key={period.value} value={period.value}>
                                  {period.label}
                                </option>
                              ))}
                            </Select>
                          </Field>
                        </>
                      ) : null}
                    </div>
                  ))}
                </FormSection>

                <FormSection
                title="Other charges"
                hint="What is payable on top, and which price it belongs to."
              >
                <UnitCharges
                  offers={offers}
                  prices={{
                    rent: minor(rentInput),
                    lease: minor(leaseInput),
                    sale: minor(saleInput),
                    short_let: minor(shortLetInput),
                  }}
                  value={charges}
                  onChange={setCharges}
                />
              </FormSection>
              </div>

              <div className="space-y-6">
                {/* Two fields, one heading. Given a section each, the heading and the field's own label
                    said the same thing twice with a rule between them. */}
                <FormSection title="The rest of it">
                  {/*
                    Photographs, where the listing is being written rather than in a tab of their own.
                    On a new unit they are held in the browser and posted the moment the unit has an id
                    — the server's rule that a photograph needs something to hang on is not a reason to
                    make somebody save, navigate back and pick the same files again.
                  */}
                  <Field
                    label="Photographs"
                    group
                    hint="The first is the one people see. Drag to change which."
                  >
                    {editing && id ? (
                      <UnitPhotos unitId={id} />
                    ) : (
                      <PendingUnitPhotos files={pendingPhotos} onChange={setPendingPhotos} />
                    )}
                  </Field>

                  <Field
                    label="Description"
                    htmlFor="description"
                    hint="Shown on the public listing, under what the place has."
                  >
                    {/* Six rows, not three. This is where the advert is actually written, and
                        three rows showed a sentence and a half of what somebody had typed. */}
                    <Textarea
                      id="description"
                      name="description"
                      rows={6}
                      placeholder="Anything a viewer should know."
                      defaultValue={String(unit.description ?? "")}
                    />
                  </Field>

                  <Field
                    label={`${titleCase(vocab.landlord)} of this ${vocab.unit}`}
                    htmlFor="landlordPartyId"
                    hint={`Leave blank when the ${vocab.property} ${vocab.landlord} owns it. Set it when this ${vocab.unit} belongs to someone else — a block is often sold flat by flat.`}
                  >
                    <SearchSelect
                      id="landlordPartyId"
                      value={ownerId}
                      onChange={(next) => setOwnerId(next)}
                      search={searchParties("landlord")}
                      resolve={resolveParty}
                      placeholder={`Same as the ${vocab.property}`}
                      emptyMessage={`No ${vocab.landlord} matched.`}
                    />
                  </Field>
                </FormSection>

                <FormSection
                  title="What it has"
                  hint="These are what somebody decides on before they read a word of the description."
                >
                  <AmenityPicker value={amenities} onChange={setAmenities} />
                </FormSection>
              </div>
            </div>

            {/*
              The buttons stay on the screen, and they span it.

              `.action-bar` pins them to the window rather than to the card — see the stylesheet
              for why it is the content area's width and not this card's. It is still inside the
              form, so the submit button submits; being pinned is a matter of where it is painted,
              not of what it belongs to.
            */}
            <div className="action-bar border-t border-ink-200 bg-white/95 backdrop-blur">
              {/* **Full width, buttons from the left.** They used to be centred inside a
                  `max-w-[1400px]` column so Save sat under the first field — which reads as a
                  floating strip with the page showing through on both sides on a wide window, and
                  the operator reported it repeatedly. The bar is the row now: the surface runs the
                  whole width and the controls begin at the left edge, where the eye starts. */}
              <div className="px-4 sm:px-6">
                {/* **One row on a phone, centred on a wide screen.**

                    Four faults in a row taught this shape. Four `nowrap` buttons in a non-wrapping
                    row ran off the side — Delete sat 84px past the right edge at 390px, unreachable.
                    Stacking them fixed that and made the bar 124px tall; the bar is *fixed*, so it
                    then covered a seventh of the screen permanently and hid the amenity chips.

                    So the phone gets **one row**: Save takes the space, Cancel takes what it needs,
                    and Delete is its icon alone — asked for by name, and the one button here whose
                    meaning survives without its label. Duplicating is the only thing held back to
                    `sm`, because it is the one action nobody needs in a hurry. */}
                <div className="flex flex-wrap items-center gap-2 py-3 sm:justify-center sm:gap-3 sm:py-4">
                  <Button type="submit" className="flex-1 justify-center sm:flex-none sm:basis-auto" disabled={save.isPending || !targetProperty}>
                    {save.isPending ? "Saving…" : editing ? "Save changes" : `Add ${vocab.unit}`}
                  </Button>
                  <Button variant="secondary" className="justify-center sm:basis-auto" onClick={() => navigate(-1)}>
                    Cancel
                  </Button>

                  {/* Everything comes across but the name, the tenancy and the listing switch — see
                      ADR-0065. The copy opens for editing, which is where the door number gets
                      changed. */}
                  {editing ? (
                    <Button
                      variant="ghost"
                      className="hidden justify-center sm:inline-flex sm:basis-auto"
                      disabled={duplicate.isPending || save.isPending}
                      onClick={() => duplicate.mutate(undefined as never)}
                    >
                      <IconCopy size={16} />
                      {duplicate.isPending ? "Duplicating…" : `Duplicate this ${vocab.unit}`}
                    </Button>
                  ) : null}

                  {/*
                    Removing it, at the far end and in its own colour.

                    Apart from the others because it is the one press here that cannot be undone by
                    pressing something else, and only offered to a role that may actually do it —
                    the API refuses either way, but a button that always refuses teaches people to
                    ignore refusals.
                  */}
                  {editing && can("property.delete") ? (
                    <Button
                      variant="danger"
                      className="justify-center sm:ml-4 sm:shrink-0 sm:basis-auto"
                      aria-label="Delete"
                      disabled={remove.isPending || save.isPending}
                      onClick={() => {
                        if (window.confirm(`Remove ${unit.label ?? `this ${vocab.unit}`}? Its photographs go with it.`)) {
                          remove.mutate(undefined as never);
                        }
                      }}
                    >
                      <IconTrash size={16} />
                      {/* The word is kept from `sm` up and dropped below it. A bin is one of the
                          few marks that reads on its own, and the row has to fit a phone. */}
                      <span className="hidden sm:inline">{remove.isPending ? "Removing…" : "Delete"}</span>
                    </Button>
                  ) : null}
                </div>
              </div>
            </div>
          </form>
        </Card>

        {!editing ? (
          <Modal open={bulkOpen} onClose={() => setBulkOpen(false)} title={`Add many ${vocab.units}`}>
            <p className="-mt-2 mb-4 text-sm text-ink-500">
              A numbered run, all with the same {vocab.rent}. Existing labels are skipped, so
              re-running is safe.
            </p>
            <form onSubmit={submitBulk} className="space-y-4">
              <Refusal error={bulk.error} message={bulk.errorMessage} />

              {/* The same decision as on the single form, in the same place, so a run of flats
                  cannot appear on the public site by a default nobody was shown. */}
              <div
                className={`flex items-start gap-4 rounded-[var(--radius-surface)] border p-4 transition-colors ${
                  bulkListed ? "border-brand-300 bg-white" : "border-ink-200 bg-ink-50"
                }`}
              >
                <span
                  className={`flex size-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                    bulkListed ? "bg-brand-100 text-brand-700" : "bg-ink-200 text-ink-500"
                  }`}
                >
                  {bulkListed ? <IconEye size={18} /> : <IconEyeOff size={18} />}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-900">List them publicly while vacant</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-ink-600">
                    {bulkListed
                      ? "Every one in the run, for as long as it is empty."
                      : "None of them will appear on the public page."}
                  </p>
                </div>

                <Switch checked={bulkListed} onChange={setBulkListed} label="List them publicly while vacant" />
              </div>

              <Field label="Label prefix" htmlFor="prefix" required>
                <Input id="prefix" name="prefix" required placeholder="Flat" defaultValue="Flat" />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="From" htmlFor="from" required>
                  <Input id="from" name="from" inputMode="numeric" required placeholder="1" defaultValue="1" />
                </Field>
                <Field label="To" htmlFor="to" required>
                  <Input id="to" name="to" inputMode="numeric" required placeholder="12" defaultValue="12" />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field label="On the market as" group>
                    <OfferPicker value={bulkOffers} onChange={setBulkOffers} />
                  </Field>
                </div>

                <Field label={`${titleCase(vocab.rent)} each`} htmlFor="bulkRent">
                  <MoneyInput symbol id="bulkRent" name="bulkRent" placeholder="3,500,000" />
                </Field>
                <Field label="Paid" htmlFor="bulkCycle">
                  <TermInput
                    idPrefix="bulkCycle"
                    cycle={bulkCycle}
                    every={bulkEvery}
                    onCycle={setBulkCycle}
                    onEvery={setBulkEvery}
                  />
                </Field>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Field
                    label={`${titleCase(vocab.landlord)} of these ${vocab.units}`}
                    htmlFor="bulkLandlord"
                    hint={`Blank means the ${vocab.property} ${vocab.landlord}. Set it when a run of ${vocab.units} was sold to one owner.`}
                  >
                    <SearchSelect
                      id="bulkLandlord"
                      value={bulkOwnerId}
                      onChange={(next) => setBulkOwnerId(next)}
                      search={searchParties("landlord")}
                      resolve={resolveParty}
                      placeholder={`Same as the ${vocab.property}`}
                      emptyMessage={`No ${vocab.landlord} matched.`}
                    />
                  </Field>
                </div>

                <div className="sm:col-span-2">
                  <Field label="What they have" group hint="Applied to every one in the run.">
                    <AmenityPicker value={bulkAmenities} onChange={setBulkAmenities} />
                  </Field>
                </div>

                <Field label="Bedrooms each" htmlFor="bulkBedrooms">
                  <Input id="bulkBedrooms" name="bulkBedrooms" inputMode="numeric" placeholder="3" />
                </Field>
                <Field label="Bathrooms each" htmlFor="bulkBathrooms">
                  <Input id="bulkBathrooms" name="bulkBathrooms" inputMode="numeric" placeholder="2" />
                </Field>
                <Field label="Toilets each" htmlFor="bulkToilets">
                  <Input id="bulkToilets" name="bulkToilets" inputMode="numeric" placeholder="3" />
                </Field>
              </div>

              {/*
                The same charges on every unit in the run, which is what a block of identical flats
                actually has. The rent is typed above but not held, so a percentage here shows the
                rate rather than the figure — the figure lands on each unit as it is created, and
                is on its own form afterwards.
              */}
              <FormSection title="Other charges" hint="Applied to every one in the run.">
                <UnitCharges offers={bulkOffers} prices={{}} value={bulkCharges} onChange={setBulkCharges} />
              </FormSection>

              <div className="flex gap-2 pt-1">
                <Button type="submit" disabled={bulk.isPending || !targetProperty}>
                  {bulk.isPending ? "Creating…" : `Create ${vocab.units}`}
                </Button>
                <Button variant="secondary" onClick={() => setBulkOpen(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Modal>
        ) : null}
      </div>
    </div>
  );
}

/**
 * What the place has.
 *
 * **Ordered by what decides a viewing, not alphabetically.** A borehole, a generator and a gated
 * street are the first three questions asked about a flat in this market; a cinema room is the
 * twentieth. The common ones are shown and the rest are folded away, because thirty tick boxes
 * get skipped and then the listing has none — which is worse than a shorter list, since a listing
 * with no amenities reads as a listing with nothing.
 *
 * The marks are the same ones the public listing draws, so what an agency ticks here is literally
 * what a viewer sees.
 */
export function AmenityPicker({ value, onChange }: { value: readonly string[]; onChange: (next: string[]) => void }) {
  const [showAll, setShowAll] = useState(() => AMENITIES.some((entry) => !entry.common && value.includes(entry.value)));
  const shown = showAll ? AMENITIES : COMMON_AMENITIES;
  const hidden = AMENITIES.length - COMMON_AMENITIES.length;

  const toggle = (amenity: string) =>
    onChange(value.includes(amenity) ? value.filter((entry) => entry !== amenity) : [...value, amenity]);

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {shown.map((entry) => {
          const chosen = value.includes(entry.value);
          return (
            <button
              key={entry.value}
              type="button"
              aria-pressed={chosen}
              onClick={() => toggle(entry.value)}
              className={`flex items-center gap-2 rounded-[var(--radius-control)] border px-3 py-2 text-[13px] transition-colors ${
                chosen
                  ? "border-brand-500 bg-brand-50 font-semibold text-brand-800"
                  : "border-ink-200 bg-white text-ink-600 hover:border-ink-300 hover:bg-ink-50"
              }`}
            >
              <AmenityIcon amenity={entry.value} size={17} className={chosen ? "text-brand-600" : "text-ink-400"} />
              {entry.label}
            </button>
          );
        })}
      </div>

      {showAll ? null : (
        <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => setShowAll(true)}>
          <IconPlus size={15} />
          {hidden} more
        </Button>
      )}
    </div>
  );
}
